import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthenticationService } from '../services/authentication.service';
import { LogCategory, LogCoreService, LoggerService, LogType } from './logger.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {

    constructor(
        public auth: AuthenticationService,
        private logger: LoggerService
    ) {
        this.log("constructor", null, LogCategory.Constructor);
    }
    private log(method: string = null, message = null, category: LogCategory = LogCategory.Default): void {
        method = (method) ? '.' + method + '()' : '';
        message = (message) ? message.toString() : '';
        this.logger.try(this.constructor.name + method + ': ' + message, LogType.CoreService, LogCoreService.TokenInterceptor, category);
    }

    public intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        this.log("intercept", req.method + " " + req.url);

        // If this is a POST to http://localhost:54919/auth then we're trying to get a token,
        // so there's no need to add one. Go ahead and let the request through. (If it's a GET
        // to /auth then we're refreshing a token, so we do need to add the token.)
        if ((req.method == 'POST') && (req.url.endsWith('/auth'))) {
            this.log("intercept", "...posting to /auth, so pass through");
            return next.handle(req);
        }
        
        // Append the token and process the request 
        return next.handle(this.addToken(req))
            .pipe(
                catchError(error => {
                    if (error instanceof HttpErrorResponse) {
                        this.log("intercept", "intercept catching error; referring to handler: " + (<HttpErrorResponse>error).status);
                        switch ((<HttpErrorResponse>error).status) {
                            case 401: // only catch unauthorized, and rethrow everything else 
                                return this.handle401(req, next);
                            default:
                                return throwError(error);
                        }
                    } else {
                        return throwError(error);
                    }
                })
            );
    }

    // Handles an Unauthorized (401) Http Response by attempting to refresh the token, if necessary. 
    private handle401(req: HttpRequest<any>, next: HttpHandler) : Observable<HttpEvent<any>> {
        this.log("handle401Error");

        return this.auth.tryRefreshToken$().pipe(
            switchMap(
                (data) => {
                    this.log("handle401Error", "result: " + data);
                    return next.handle(this.addToken(req));
                }));
    }

    // Adds the auth bearer token to the request header (always) and the now override header (if required)
    private addToken(req: HttpRequest<any>): HttpRequest<any> {

        // Default auth bearer header 
        let headers = { Authorization: 'Bearer ' + this.auth.token.value };

        // If we're supposed to override the DateTime.Now, then add it as a header here
        let nowOverride = localStorage.getItem('nowOverride');
        if (nowOverride) {
            headers["Kfbs-Now"] = nowOverride;
            this.log("addToken", "Overriding DateTime.Now to " + nowOverride, LogCategory.Default);
        }

        return req.clone({ setHeaders: headers });
    }
}
